import { z } from 'zod';
import { slug, storedVisibility } from './common.js';
import { fileKind } from './kinds.js';

export const layoutRule = z.object({
  /** Glob relative to the workspace root (picomatch syntax, braces allowed). */
  glob: z.string().min(1),
  kind: fileKind,
  /** A CSV (glob) whose rows give metadata for matching files (column `file` or `path` names the file). */
  metaFrom: z.string().min(1).optional(),
  /** Defaults to `publishable`. */
  visibility: storedVisibility.optional(),
});
export type LayoutRule = z.infer<typeof layoutRule>;

/** `p2b.json` at the workspace root (DESIGN §7.1). Unknown top-level keys are allowed for forward compatibility. */
export const p2bJson = z.looseObject({
  $schema: z.string().optional(),
  platform: z.url(),
  org: slug,
  project: slug,
  /** Kit version range the workspace expects, e.g. ">=2.0". */
  kit: z.string().optional(),
  layout: z.array(layoutRule).default([]),
  /** Globs that must never be uploaded (licensed standards and the like). The CLI refuses them. */
  restricted: z.array(z.string().min(1)).default([]),
  /** Globs the CLI never tracks. */
  ignore: z.array(z.string().min(1)).default([]),
});
export type P2bJson = z.infer<typeof p2bJson>;

export const P2B_JSON = 'p2b.json';

/** The layout from DESIGN §7.1, used when the kit's project template is not at hand. */
export const DEFAULT_LAYOUT: LayoutRule[] = [
  { glob: '**/page.json', kind: 'page', visibility: 'internal' },
  { glob: 'models/*/reports/**', kind: 'report', visibility: 'internal' },
  { glob: 'drawings/**/MANIFEST.csv', kind: 'source-index' },
  { glob: 'drawings/**/*.{pdf,tif,tiff}', kind: 'source-drawing', metaFrom: 'drawings/**/MANIFEST.csv' },
  { glob: 'models/*/overlays/**', kind: 'overlay' },
  { glob: 'models/*/reading/owner_photos_*/**', kind: 'photo', visibility: 'internal' },
  { glob: 'models/*/src/**', kind: 'model-source' },
  { glob: 'models/*/{model.js,spaces.json,areas.json,features.json}', kind: 'model-build' },
  { glob: 'models/*/ifc/*.ifc', kind: 'ifc' },
  { glob: 'pointclouds/**/*.{e57,las,laz}', kind: 'point-cloud' },
];

export function defaultP2bJson(platform: string, org: string, project: string): P2bJson {
  return {
    $schema: 'https://bim.mynstra.is/schema/p2b.schema.json',
    platform,
    org,
    project,
    kit: '>=2.0',
    layout: DEFAULT_LAYOUT,
    restricted: ['ÍST 50_1998.pdf', '**/ÍST 50*.pdf'],
    ignore: [
      '**/_wip/**',
      '**/enhanced/_flat/**',
      '**/.DS_Store',
      '.p2b/**',
      'node_modules/**',
      '.git/**',
      '.gitignore',
      '.claude/**',
    ],
  };
}
